'use client';

import { type RefObject, useEffect, useRef } from 'react';
import { SCRAMBLE_CHARSET, SCRAMBLE_CHARSET_UPPER, scrambleTextOneShot } from '@/lib/textEffects/scramble';

export interface ScrambleInOptions {
  active: boolean;
  staggerMs?: number;
  durationMs?: readonly [number, number];
  uppercase?: boolean;
  onDone?: () => void;
}

const SELECTOR = '[data-scramble-in]';
const DEFAULT_DURATION_MS = [1000, 1500] as const;

export function useScrambleIn(rootRef: RefObject<HTMLElement | null>, options: ScrambleInOptions): void {
  const { active, staggerMs = 0, durationMs = DEFAULT_DURATION_MS, uppercase = false, onDone } = options;
  const [minMs, maxMs] = durationMs;
  const playedRootRef = useRef<HTMLElement | null>(null);
  const onDoneRef = useRef(onDone);

  useEffect(() => {
    onDoneRef.current = onDone;
  });

  useEffect(() => {
    const root = rootRef.current;
    if (!active || !root || playedRootRef.current === root) return;
    playedRootRef.current = root;

    const elements = Array.from(root.querySelectorAll<HTMLElement>(SELECTOR));
    const runMs = minMs + Math.random() * (maxMs - minMs);
    const charset = uppercase ? SCRAMBLE_CHARSET_UPPER : SCRAMBLE_CHARSET;
    const timeoutIds: number[] = [];
    const cancels: (() => void)[] = [];
    let remaining = elements.length;

    const finishOne = () => {
      remaining -= 1;
      if (remaining === 0) onDoneRef.current?.();
    };

    if (elements.length === 0) onDoneRef.current?.();

    elements.forEach((el, i) => {
      timeoutIds.push(
        window.setTimeout(() => {
          cancels.push(scrambleTextOneShot(el, { durationMs: runMs, charset, onDone: finishOne }));
        }, i * staggerMs),
      );
    });

    return () => {
      timeoutIds.forEach((id) => window.clearTimeout(id));
      cancels.forEach((cancel) => cancel());
      if (remaining > 0) playedRootRef.current = null;
    };
  }, [active, rootRef, staggerMs, minMs, maxMs, uppercase]);
}
